import api from "./api";

export interface Income {
  id: number;
  bookingId: number;
  amount: number;
  paymentDate: string;
  paymentMethod:
    | "cheque"
    | "neft"
    | "imps"
    | "rtgs"
    | "upi"
    | "mb"
    | "transfer";
  referenceNumber?: string;
  bankName?: string;
  description?: string;
  receiptName?: string; // Name of the person who gave the payment
  chequeClearanceDate?: string; // Auto-filled when approved
  receiptNo?: string; // Receipt number
  chequeReceivedDate?: string; // Date when cheque was received
  instrumentDate?: string; // Instrument/cheque date
  chequeDepositDate?: string; // Date when cheque was deposited
  remarks?: string; // Additional remarks or reference information
  paymentTerms?: string; // Payment terms: Down Payment, Loan, Installments, Mixed Payment
  status: "approval_pending" | "approved" | "rejected";
  approvedBy?: number;
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  booking: {
    id: number;
    bookingId: string;
    project: {
      id: number;
      name: string;
    };
    unit: {
      id: number;
      unitNo: string;
      wing: string;
    };
    applicants: Array<{
      id: number;
      firstName: string;
      lastName: string;
      isPrimary: boolean;
    }>;
  };
  returnAllocations?: ReturnAllocation[];
}

export interface ReturnAllocation {
  id: number;
  returnIncomeId: number;
  sourceIncomeId: number;
  allocatedAmount: number;
  sourceIncome: {
    id: number;
    amount: number;
    paymentDate: string;
    referenceNumber: string;
    receiptNo: string;
  };
}

export interface AvailableIncome {
  id: number;
  amount: number;
  remainingBalance: number;
  paymentDate: string;
  receiptNo: string;
  referenceNumber: string;
  paymentMethod: string;
}

export interface CreateIncomeData {
  bookingId: number;
  amount: number;
  paymentDate: string;
  paymentMethod:
    | "cheque"
    | "neft"
    | "imps"
    | "rtgs"
    | "upi"
    | "mb"
    | "transfer";
  referenceNumber?: string;
  bankName?: string;
  description?: string;
  receiptName?: string;
  receiptNo?: string;
  chequeReceivedDate?: string;
  instrumentDate?: string;
  chequeDepositDate?: string;
  remarks?: string;
  paymentTerms?: string;
}

export interface IncomeFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  project?: string;
  paymentMethod?: string;
}

export interface IncomeResponse {
  incomes: Income[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ChequePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

class IncomeService {
  // Get all incomes with filters
  async getIncomes(filters: IncomeFilters = {}): Promise<IncomeResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/incomes?${params.toString()}`);
    return response.data;
  }

  // Get incomes pending approval with optional filters
  async getPendingIncomes(
    params: {
      page?: number;
      limit?: number;
      projectId?: number | string;
      paymentMethod?: string;
      receivedStart?: string;
      receivedEnd?: string;
      instrumentStart?: string;
      instrumentEnd?: string;
      unit?: string;
      amountMin?: number | string;
      amountMax?: number | string;
      showAllCheques?: boolean;
    } = {}
  ): Promise<IncomeResponse> {
    const searchParams = new URLSearchParams();
    const {
      page = 1,
      limit = 20,
      projectId,
      paymentMethod,
      receivedStart,
      receivedEnd,
      instrumentStart,
      instrumentEnd,
      unit,
      amountMin,
      amountMax,
      showAllCheques,
    } = params;
    searchParams.set("page", String(page));
    searchParams.set("limit", String(limit));
    if (projectId !== undefined && projectId !== null && projectId !== "")
      searchParams.set("projectId", String(projectId));
    if (paymentMethod) searchParams.set("paymentMethod", paymentMethod);
    if (receivedStart) searchParams.set("receivedStart", receivedStart);
    if (receivedEnd) searchParams.set("receivedEnd", receivedEnd);
    if (instrumentStart) searchParams.set("instrumentStart", instrumentStart);
    if (instrumentEnd) searchParams.set("instrumentEnd", instrumentEnd);
    if (unit) searchParams.set("unit", unit);
    if (amountMin !== undefined && amountMin !== null && amountMin !== "")
      searchParams.set("amountMin", String(amountMin));
    if (amountMax !== undefined && amountMax !== null && amountMax !== "")
      searchParams.set("amountMax", String(amountMax));
    if (showAllCheques) searchParams.set("showAllCheques", "true");

    const response = await api.get(
      `/incomes/pending?${searchParams.toString()}`
    );
    return response.data;
  }

  // Get income by ID
  async getIncomeById(id: number): Promise<Income> {
    const response = await api.get(`/incomes/${id}`);
    return response.data;
  }

  // Get incomes for a specific booking
  async getIncomesByBooking(bookingId: number): Promise<Income[]> {
    const response = await api.get(`/incomes/booking/${bookingId}`);
    return response.data;
  }

  // Create new income
  async createIncome(data: CreateIncomeData): Promise<Income> {
    const response = await api.post("/incomes", data);
    return response.data;
  }

  // Record sale-deed income found AFTER a booking was cancelled.
  // Directly approved, cheque only, mandatory reason.
  async createPostCancellationIncome(data: {
    bookingId: number;
    amount: number;
    paymentDate: string;
    referenceNumber: string; // cheque number
    bankName: string;
    instrumentDate?: string;
    chequeReceivedDate?: string;
    receiptNo?: string;
    reason: string;
  }): Promise<Income> {
    const response = await api.post("/incomes/post-cancellation", data);
    return response.data;
  }

  // Update income
  async updateIncome(
    id: number,
    data: Partial<CreateIncomeData>
  ): Promise<Income> {
    const response = await api.put(`/incomes/${id}`, data);
    return response.data;
  }

  // Approve multiple incomes
  async approveIncomes(
    incomeIds: number[]
  ): Promise<{ message: string; approvedCount: number }> {
    const response = await api.post("/incomes/approve", {
      incomeIds,
    });
    return response.data;
  }

  // Reject income
  async rejectIncome(
    id: number,
    rejectionReason: string
  ): Promise<Income> {
    const response = await api.put(`/incomes/${id}/reject`, {
      rejectionReason,
    });
    return response.data;
  }

  // Delete income
  async deleteIncome(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/incomes/${id}`);
    return response.data;
  }

  // Get missed cheque deposits (paginated)
  async getMissedChequeDeposits(params: {
    page?: number;
    limit?: number;
    project?: string;
  } = {}): Promise<{ data: Income[]; pagination: ChequePagination }> {
    const response = await api.get("/incomes/cheques/missed", { params });
    return response.data;
  }

  // Get upcoming cheque deposits (paginated). showAll=false → only today+tomorrow.
  async getUpcomingChequeDeposits(params: {
    page?: number;
    limit?: number;
    project?: string;
    showAll?: boolean;
  } = {}): Promise<{ data: Income[]; pagination: ChequePagination }> {
    const response = await api.get("/incomes/cheques/upcoming", { params });
    return response.data;
  }

  // Get payment methods
  getPaymentMethods() {
    return [
      { value: "cheque", label: "Cheque" },
      { value: "rtgs", label: "RTGS" },
      { value: "neft", label: "NEFT" },
      { value: "imps", label: "IMPS" },
      { value: "upi", label: "UPI" },
      { value: "mb", label: "MB" },
      { value: "transfer", label: "Transfer" },
      { value: "other", label: "Other" },
    ];
  }

  // Get status options
  getStatusOptions() {
    return [
      { value: "approval_pending", label: "Pending Approval" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
    ];
  }

  // Format amount for display
  formatAmount(amount: number): string {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  // Format date for display
  formatDate(date: string): string {
    const d = new Date(date);
    // Use UTC methods to avoid timezone conversion issues
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  // Get customer name from booking
  getCustomerName(income: Income): string {
    if (!income.booking || !income.booking.applicants) return "N/A";
    const primaryApplicant =
      income.booking.applicants.find((a) => a.isPrimary) ||
      income.booking.applicants[0];
    if (!primaryApplicant) return "N/A";
    const anyA: any = primaryApplicant as any;
    const parts = [
      anyA.firstName || "",
      anyA.fatherHusbandName || anyA.middleName || anyA.middle || "",
      anyA.lastName || anyA.surname || "",
    ].filter(Boolean);
    return parts.length ? parts.join(" ") : "N/A";
  }

  // Get unit display
  getUnitDisplay(income: Income): string {
    if (!income.booking || !income.booking.unit) return "N/A";
    const { unit } = income.booking;
    // Handle null/undefined wing properly - just show unit number if no wing
    return unit.wing ? `${unit.wing}-${unit.unitNo}` : unit.unitNo;
  }

  // Get payment method display
  getPaymentMethodDisplay(method: string): string {
    const methods = this.getPaymentMethods();
    const found = methods.find((m) => m.value === method);
    return found ? found.label : method.toUpperCase();
  }

  // Get status display with color
  getStatusDisplay(status: string): { text: string; className: string } {
    switch (status) {
      case "approval_pending":
        return { text: "Pending Approval", className: "status-pending" };
      case "approved":
        return { text: "Approved", className: "status-approved" };
      case "rejected":
        return { text: "Rejected", className: "status-rejected" };
      default:
        return { text: status, className: "status-unknown" };
    }
  }

  // Get available incomes for return allocation
  async getAvailableIncomesForReturn(bookingId: number): Promise<AvailableIncome[]> {
    const response = await api.get(`/incomes/booking/${bookingId}/available-for-return`);
    return response.data;
  }

  // Create return income with allocations
  async createReturnIncome(data: {
    bookingId: number;
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    referenceNumber: string;
    bankName?: string;
    receiptNo?: string;
    chequeReceivedDate?: string;
    instrumentDate?: string;
    chequeDepositDate?: string;
    remarks?: string;
    paymentTerms?: string;
    allocations: Array<{ sourceIncomeId: number; allocatedAmount: number }>;
  }): Promise<Income> {
    const response = await api.post("/incomes/return", data);
    return response.data;
  }

  // Get return allocations for a specific return income
  async getReturnAllocations(returnIncomeId: number): Promise<ReturnAllocation[]> {
    const response = await api.get(`/incomes/return/${returnIncomeId}/allocations`);
    return response.data;
  }

  // Get return summary for a booking
  async getBookingReturnSummary(bookingId: number): Promise<any[]> {
    const response = await api.get(`/incomes/booking/${bookingId}/return-summary`);
    return response.data;
  }
}

export const incomeService = new IncomeService();
